import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { api } from '../services/api';
import { FeedbackModal } from './FeedbackModal';

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TemplateButton {
  type: 'PHONE_NUMBER' | 'URL' | 'QUICK_REPLY';
  text: string;
  phone_number?: string;
  url?: string;
}

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const bodyTextRef = React.useRef<HTMLTextAreaElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    category: 'UTILITY' as 'MARKETING' | 'UTILITY' | 'AUTHENTICATION',
    language: 'pt_BR',
    bodyText: '',
    bodyExamples: [''],
    headerText: '',
    footerText: '',
    buttons: [] as TemplateButton[],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const components: any[] = [];

      // Add header if provided
      if (formData.headerText.trim()) {
        components.push({
          type: 'HEADER',
          format: 'TEXT',
          text: formData.headerText,
        });
      }

      // Add body (required)
      const bodyComponent: any = {
        type: 'BODY',
        text: formData.bodyText,
      };

      // Add examples if there are variables
      const variableCount = (formData.bodyText.match(/\{\{[0-9]+\}\}/g) || []).length;
      if (variableCount > 0 && formData.bodyExamples.some(ex => ex.trim())) {
        bodyComponent.example = {
          body_text: [formData.bodyExamples.filter(ex => ex.trim())],
        };
      }

      components.push(bodyComponent);

      // Add footer if provided
      if (formData.footerText.trim()) {
        components.push({
          type: 'FOOTER',
          text: formData.footerText,
        });
      }

      // Add buttons if provided
      if (formData.buttons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons: formData.buttons,
        });
      }

      const payload = {
        name: formData.name,
        category: formData.category,
        language: formData.language,
        components,
        parameter_format: 'POSITIONAL',
        allow_category_change: true,
      };

      await api.createTemplate(payload);
      setFeedbackType('success');
      setFeedbackMessage('Template criado com sucesso! Aguarde a aprovação da Meta.');
      setShowFeedback(true);
      onSuccess();
      resetForm();
    } catch (error: any) {
      setFeedbackType('error');
      setFeedbackMessage(error.response?.data?.error || 'Falha ao criar template');
      setShowFeedback(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedbackClose = () => {
    setShowFeedback(false);
    if (feedbackType === 'success') {
      onClose();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'UTILITY',
      language: 'pt_BR',
      bodyText: '',
      bodyExamples: [''],
      headerText: '',
      footerText: '',
      buttons: [],
    });
    setError(null);
  };

  const addButton = () => {
    setFormData({
      ...formData,
      buttons: [...formData.buttons, { type: 'QUICK_REPLY', text: '' }],
    });
  };

  const removeButton = (index: number) => {
    setFormData({
      ...formData,
      buttons: formData.buttons.filter((_, i) => i !== index),
    });
  };

  const updateButton = (index: number, field: string, value: string) => {
    const newButtons = [...formData.buttons];
    newButtons[index] = { ...newButtons[index], [field]: value };
    setFormData({ ...formData, buttons: newButtons });
  };

  const addExample = () => {
    setFormData({
      ...formData,
      bodyExamples: [...formData.bodyExamples, ''],
    });
  };

  const removeExample = (index: number) => {
    setFormData({
      ...formData,
      bodyExamples: formData.bodyExamples.filter((_, i) => i !== index),
    });
  };

  const updateExample = (index: number, value: string) => {
    const newExamples = [...formData.bodyExamples];
    newExamples[index] = value;
    setFormData({ ...formData, bodyExamples: newExamples });
  };

  const insertVariable = () => {
    const textarea = bodyTextRef.current;
    if (!textarea) return;

    // Count existing variables
    const existingVariables = (formData.bodyText.match(/\{\{[0-9]+\}\}/g) || []).length;
    const nextVariable = `{{${existingVariables + 1}}}`;

    // Get cursor position
    const cursorPosition = textarea.selectionStart;
    const textBefore = formData.bodyText.substring(0, cursorPosition);
    const textAfter = formData.bodyText.substring(cursorPosition);

    // Insert variable at cursor position
    const newText = textBefore + nextVariable + textAfter;
    setFormData({ ...formData, bodyText: newText });

    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      const newCursorPosition = cursorPosition + nextVariable.length;
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  if (!isOpen) return null;

  return (
    <>
      <FeedbackModal
        isOpen={showFeedback}
        onClose={handleFeedbackClose}
        type={feedbackType}
        title={feedbackType === 'success' ? 'Sucesso!' : 'Erro'}
        message={feedbackMessage}
      />
      <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-full max-w-2xl z-50">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Criar Novo Template
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome do Template *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="ex: order_confirmation"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use apenas letras minúsculas, números e underscore
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Categoria *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  >
                    <option value="UTILITY">Utilidade</option>
                    <option value="MARKETING">Marketing</option>
                    <option value="AUTHENTICATION">Autenticação</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Idioma *
                  </label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  >
                    <option value="pt_BR">Português (BR)</option>
                    <option value="en_US">Inglês (US)</option>
                    <option value="es_ES">Espanhol</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cabeçalho (Opcional)
                </label>
                <input
                  type="text"
                  value={formData.headerText}
                  onChange={(e) => setFormData({ ...formData, headerText: e.target.value })}
                  placeholder="ex: Confirmação de Pedido"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Corpo da Mensagem *
                  </label>
                  <button
                    type="button"
                    onClick={insertVariable}
                    className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition"
                  >
                    + Inserir Variável
                  </button>
                </div>
                <textarea
                  ref={bodyTextRef}
                  required
                  value={formData.bodyText}
                  onChange={(e) => setFormData({ ...formData, bodyText: e.target.value })}
                  placeholder="Olá {{1}}, seu pedido {{2}} foi confirmado!"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use o botão acima ou digite {"{{1}}"}, {"{{2}}"}, etc. para variáveis dinâmicas
                </p>
              </div>

              {formData.bodyText.includes('{{') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Exemplos de Variáveis
                  </label>
                  {formData.bodyExamples.map((example, index) => (
                    <div key={index} className="flex space-x-2 mb-2">
                      <input
                        type="text"
                        value={example}
                        onChange={(e) => updateExample(index, e.target.value)}
                        placeholder={`Exemplo para {{${index + 1}}}`}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      />
                      {formData.bodyExamples.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeExample(index)}
                          className="px-3 py-2 text-red-600 hover:text-red-700 dark:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addExample}
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center space-x-1"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Adicionar exemplo</span>
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rodapé (Opcional)
                </label>
                <input
                  type="text"
                  value={formData.footerText}
                  onChange={(e) => setFormData({ ...formData, footerText: e.target.value })}
                  placeholder="ex: Equipe de Atendimento"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Botões (Opcional)
                </label>
                {formData.buttons.map((button, index) => (
                  <div key={index} className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 mb-2">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Botão {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeButton(index)}
                        className="text-red-600 hover:text-red-700 dark:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      <select
                        value={button.type}
                        onChange={(e) => updateButton(index, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                      >
                        <option value="QUICK_REPLY">Resposta Rápida</option>
                        <option value="PHONE_NUMBER">Telefone</option>
                        <option value="URL">URL</option>
                      </select>
                      <input
                        type="text"
                        value={button.text}
                        onChange={(e) => updateButton(index, 'text', e.target.value)}
                        placeholder="Texto do botão"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                      />
                      {button.type === 'PHONE_NUMBER' && (
                        <input
                          type="text"
                          value={button.phone_number || ''}
                          onChange={(e) => updateButton(index, 'phone_number', e.target.value)}
                          placeholder="+5534999999999"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                        />
                      )}
                      {button.type === 'URL' && (
                        <input
                          type="text"
                          value={button.url || ''}
                          onChange={(e) => updateButton(index, 'url', e.target.value)}
                          placeholder="https://exemplo.com"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                        />
                      )}
                    </div>
                  </div>
                ))}
                {formData.buttons.length < 3 && (
                  <button
                    type="button"
                    onClick={addButton}
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center space-x-1"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Adicionar botão</span>
                  </button>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg disabled:opacity-50"
                >
                  {isLoading ? 'Criando...' : 'Criar Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};
